import React, {useCallback, useEffect, useRef, useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Typography} from "@mui/material";
import {ChevronLeft, ChevronRight, Delete} from "@mui/icons-material";
import {EDITOR_BG, HEADER_BG, PHONE_WIDTH, useViewport} from "../../pages/estimate/photo-editor/PhotoEditor";

export interface ViewerPhoto {
  id: number | string;
  url: string;
  /** Drawn beside Close while this photo is shown ("Gate 1"); else the viewer's own [PhotoViewerProps.badge]. */
  badge?: string;
  /** False: no Edit Picture on this photo, though the viewer has onEdit (Android's per-photo onEdit). */
  canEdit?: boolean;
  /** False: no delete on this photo, though the viewer has onDelete. */
  canDelete?: boolean;
  /** This photo's own "are you sure" (a gate photo on a work item is removed from that item only, SQ-716). */
  deleteConfirm?: {title: string; text: string; confirm: string};
}

export interface PhotoViewerProps {
  photos: ViewerPhoto[];
  /** The photo to open on; null keeps the viewer closed. */
  startIndex: number | null;
  onClose: () => void;
  /** Open the photo editor over the viewer; closing the editor comes back here. Absent: no Edit button. */
  onEdit?: (index: number) => void;
  /** Remove the photo (after the viewer's own "are you sure"). Absent: no delete button. */
  onDelete?: (index: number) => void;
  deleteText?: string;
  /** Names the photos for screen readers ("Gate 2 picture 1 of 3"). */
  label?: string;
  /** Drawn in the top bar beside Close for every photo (a gate's "Gate 1", SQ-657), unless a photo has its own. */
  badge?: string;
  /** The editor is open over the viewer: keep the viewer (and where it is) but out of sight, so its window never shows around the editor's. */
  hidden?: boolean;
}

const MAX_ZOOM = 5;
const DOUBLE_CLICK_ZOOM = 2.5;
const SWIPE_DISTANCE = 60;
const BAR_HEIGHT = 52;
/** Wide enough for Close, the counter and Edit Picture side by side. */
const MIN_CARD_WIDTH = 420;

/** Like the editor's window: the whole screen on a phone; elsewhere ~90% of the height and as wide as the photo needs. */
const useCardSize = (aspect: number): {width: number | string; height: number | string; full: boolean} => {
  const {w, h} = useViewport();
  if (w < PHONE_WIDTH) return {width: '100%', height: '100%', full: true};
  const maxW = w * 0.95;
  const bars = BAR_HEIGHT * 2;
  let height = h * 0.9;
  let width = aspect * (height - bars);
  if (width > maxW) {
    width = maxW;
    height = Math.min(height, width / aspect + bars);
  }
  return {width: Math.max(Math.min(MIN_CARD_WIDTH, maxW), width), height, full: false};
};

interface View {scale: number; x: number; y: number}
const AT_REST: View = {scale: 1, x: 0, y: 0};

/**
 * A record's photos full screen (SQ-572), the web twin of Android's
 * FullScreenPhotoViewer: arrows, arrow keys or a swipe move between them,
 * the scroll wheel, a trackpad or two fingers zoom about the pointer, a
 * drag pans a zoomed photo, and a double click zooms in or back out. Edit
 * and delete are only offered when the page passes them (the record is
 * editable); nothing here changes a photo by itself.
 */
export const PhotoViewer = (props: PhotoViewerProps) => {
  const {photos, startIndex, onClose, onEdit, onDelete} = props;
  const open = startIndex !== null && photos.length > 0;
  const [index, setIndex] = useState(0);
  const [view, setView] = useState<View>(AT_REST);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [aspect, setAspect] = useState(3 / 4);
  const card = useCardSize(aspect);
  const frame = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(() => {
    if (startIndex !== null) setIndex(startIndex);
  }, [startIndex]);
  // A deleted photo leaves the viewer on the next one; the last one closes it.
  useEffect(() => {
    if (startIndex === null) return;
    if (photos.length === 0) onClose();
    else if (index > photos.length - 1) setIndex(photos.length - 1);
  }, [photos.length, index, startIndex, onClose]);
  useEffect(() => setView(AT_REST), [index, startIndex]);

  const current = photos[Math.min(index, photos.length - 1)];
  // As given: a photo link is signed (SQ-735) and any added parameter voids
  // it. The editor fetches its own copy (PhotoEditor's loadImage), so this
  // <img> needs neither CORS nor a cache-busting query.
  const src = current?.url ?? '';

  const step = useCallback((by: number) => {
    if (photos.length < 2) return;
    setIndex(i => (i + by + photos.length) % photos.length);
  }, [photos.length]);

  /** Zoom by `factor` keeping the photo point under `at` (page coordinates) where it is. */
  const zoomAt = useCallback((factor: number, at: {x: number, y: number}, pan = {x: 0, y: 0}) => {
    const box = frame.current?.getBoundingClientRect();
    if (!box) return;
    setView(v => {
      const scale = Math.min(MAX_ZOOM, Math.max(1, v.scale * factor));
      const px = at.x - (box.left + box.width / 2);
      const py = at.y - (box.top + box.height / 2);
      const ratio = scale / v.scale;
      const maxX = (scale - 1) * box.width / 2;
      const maxY = (scale - 1) * box.height / 2;
      return {
        scale,
        x: Math.max(-maxX, Math.min(maxX, px + pan.x - (px - v.x) * ratio)),
        y: Math.max(-maxY, Math.min(maxY, py + pan.y - (py - v.y) * ratio)),
      };
    });
  }, []);

  // The wheel needs a non-passive listener to keep the page from scrolling.
  // A trackpad pinch arrives as a wheel event with ctrlKey.
  const setFrame = useCallback((node: HTMLDivElement | null) => {
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const speed = e.ctrlKey ? 0.01 : 0.0015;
      zoomAt(Math.exp(-e.deltaY * speed), {x: e.clientX, y: e.clientY});
    };
    frame.current?.removeEventListener('wheel', (frame.current as any).__onWheel);
    frame.current = node;
    if (node) {
      (node as any).__onWheel = onWheel;
      node.addEventListener('wheel', onWheel, {passive: false});
    }
  }, [zoomAt]);

  // Pointers: two zoom and pan, one pans a zoomed photo or swipes at 1x.
  const pointers = useRef(new Map<number, {x: number, y: number}>());
  const swipeStart = useRef<{x: number, y: number} | null>(null);
  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, {x: e.clientX, y: e.clientY});
    swipeStart.current = pointers.current.size === 1 ? {x: e.clientX, y: e.clientY} : null;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const before = pointers.current.get(e.pointerId);
    if (!before) return;
    const others: {x: number, y: number}[] = [];
    pointers.current.forEach((p, id) => { if (id !== e.pointerId) others.push(p); });
    pointers.current.set(e.pointerId, {x: e.clientX, y: e.clientY});
    if (others.length >= 1) {
      const other = others[0];
      const distBefore = Math.hypot(before.x - other.x, before.y - other.y);
      const distNow = Math.hypot(e.clientX - other.x, e.clientY - other.y);
      const mid = {x: (e.clientX + other.x) / 2, y: (e.clientY + other.y) / 2};
      const pan = {x: (e.clientX - before.x) / 2, y: (e.clientY - before.y) / 2};
      zoomAt(distBefore > 0 ? distNow / distBefore : 1, mid, pan);
    } else if (viewRef.current.scale > 1) {
      zoomAt(1, {x: e.clientX, y: e.clientY}, {x: e.clientX - before.x, y: e.clientY - before.y});
    }
  };
  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    const start = swipeStart.current;
    if (start && pointers.current.size === 0 && viewRef.current.scale <= 1 && e.pointerType !== 'mouse') {
      const dx = e.clientX - start.x;
      if (Math.abs(dx) > SWIPE_DISTANCE && Math.abs(dx) > Math.abs(e.clientY - start.y)) step(dx < 0 ? 1 : -1);
    }
    if (pointers.current.size === 0) swipeStart.current = null;
  };
  const onDoubleClick = (e: React.MouseEvent) => {
    if (viewRef.current.scale > 1.01) setView(AT_REST);
    else zoomAt(DOUBLE_CLICK_ZOOM, {x: e.clientX, y: e.clientY});
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') step(-1);
    if (e.key === 'ArrowRight') step(1);
  };

  if (!current) return null;
  const many = photos.length > 1;
  const zoomed = view.scale > 1.01;
  const badge = current.badge ?? props.badge;
  const canEdit = !!onEdit && current.canEdit !== false;
  const canDelete = !!onDelete && current.canDelete !== false;
  return (
    <Dialog open={open} onClose={onClose} fullScreen onKeyDown={onKeyDown}
            sx={props.hidden ? {visibility: 'hidden'} : undefined}
            PaperProps={{style: {background: 'rgba(0, 0, 0, 0.6)', boxShadow: 'none'}, 'aria-label': 'Photo viewer'} as object}>
      {/* The editor's kind of window (PM, 2026-10-10): on a computer a card
          as wide as the photo, its gray bars hugging it (Close and delete
          above, the counter and Edit Picture below, the corners the work
          item uses), over the dimmed page; on a phone the whole screen. A
          click on the dimmed page closes it. */}
      <Box onPointerDown={e => { if (e.target === e.currentTarget) onClose(); }}
           sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%'}}>
        <Box sx={{display: 'flex', flexDirection: 'column', width: card.width, height: card.height, bgcolor: EDITOR_BG,
                  borderRadius: card.full ? 0 : '12px', overflow: 'hidden', boxShadow: card.full ? 'none' : '0 12px 48px rgba(0,0,0,0.5)'}}>
          <Box sx={{display: 'flex', alignItems: 'center', height: BAR_HEIGHT, minHeight: BAR_HEIGHT, bgcolor: HEADER_BG, px: 0.5}}>
            <Button onClick={onClose} sx={{color: 'white', fontSize: 15, fontWeight: 600, textTransform: 'none'}}>Close</Button>
            {/* Android's GateLabelBadge: white on the gate's near-black. */}
            {badge && <Box component="span" sx={{ml: 0.5, px: '6px', py: '1px', borderRadius: '6px', bgcolor: '#161616', color: '#fff',
                                                 fontSize: 12, lineHeight: '16px', fontWeight: 700, whiteSpace: 'nowrap',
                                                 boxShadow: '0 0 0 1px rgba(255,255,255,0.35)'}}>{badge}</Box>}
            <Box sx={{flex: 1}}/>
            {canDelete && <IconButton aria-label="Delete photo" onClick={() => setConfirmDelete(true)} sx={{color: 'error.main'}}><Delete/></IconButton>}
          </Box>

          <Box sx={{position: 'relative', flex: 1, minHeight: 0}}>
            <Box ref={setFrame}
                 onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
                 onDoubleClick={onDoubleClick}
                 sx={{position: 'absolute', inset: 0, overflow: 'hidden', touchAction: 'none', userSelect: 'none',
                      cursor: zoomed ? 'grab' : 'zoom-in', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <img key={current.id} src={src} draggable={false}
                   alt={`${props.label ?? 'Photo'} ${index + 1} of ${photos.length}`}
                   onLoad={e => {
                     const img = e.currentTarget;
                     if (img.naturalWidth > 0 && img.naturalHeight > 0) setAspect(img.naturalWidth / img.naturalHeight);
                   }}
                   style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
                           transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`, transformOrigin: 'center'}}/>
            </Box>
            {many && <>
              <IconButton aria-label="Previous photo" onClick={() => step(-1)}
                          sx={{position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'white', bgcolor: 'rgba(0,0,0,0.45)'}}>
                <ChevronLeft fontSize="large"/>
              </IconButton>
              <IconButton aria-label="Next photo" onClick={() => step(1)}
                          sx={{position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: 'white', bgcolor: 'rgba(0,0,0,0.45)'}}>
                <ChevronRight fontSize="large"/>
              </IconButton>
            </>}
          </Box>

          <Box sx={{display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', height: BAR_HEIGHT, minHeight: BAR_HEIGHT,
                    bgcolor: HEADER_BG, px: 1.5, gap: 1}}>
            <Typography variant="caption" sx={{color: 'rgba(255,255,255,0.7)', lineHeight: 1.2}}>
              {card.full ? 'Pinch to zoom' : 'Scroll to zoom · double-click'}
            </Typography>
            <Typography variant="body2" sx={{color: 'white'}}>
              {many ? `${index + 1} of ${photos.length}` : ''}
            </Typography>
            <Box sx={{justifySelf: 'end'}}>
              {canEdit && <Button variant="contained" size="small" onClick={() => onEdit?.(index)}>Edit Picture</Button>}
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>{current?.deleteConfirm?.title ?? 'Delete photo?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>{current?.deleteConfirm?.text ?? props.deleteText ?? 'This removes the photo from the work item.'}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Keep</Button>
          <Button color="error" onClick={() => { setConfirmDelete(false); onDelete?.(index); }}>{current?.deleteConfirm?.confirm ?? 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
